"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import { MEMES_MANIFOLD_PROXY_ABI } from "@/abis/abis";
import { useCookieConsent } from "@/components/cookies/CookieConsentContext";
import { MANIFOLD_LAZY_CLAIM_CONTRACT } from "@/constants/constants";
import useCapacitor from "@/hooks/useCapacitor";
import { ManifoldClaimStatus, ManifoldPhase, useManifoldClaim } from "@/hooks/useManifoldClaim";
import { Time } from "@/helpers/time";
import type { Abi } from "viem";
export interface CountdownData {
  readonly title: string;
  readonly targetDate: number;
  readonly showAllowlistInfo: boolean;
  readonly showMintBtn: boolean;
  readonly isActive: boolean;
}
export type MintCountdownState = {
  type: "loading";
} | {
  type: "error";
} | {
  type: "sold_out";
} | {
  type: "finalized";
} | {
  type: "countdown";
  countdown: CountdownData;
};
interface UseMintCountdownStateOptions {
  hideMintBtn?: boolean | undefined;
  contract: string;
  chainId: number;
  abi?: Abi | undefined;
}
export function useMintCountdownState(nftId, opts) {
  const $ = _c(28);
  const [errorFromCallback, setErrorFromCallback] = useState(false);
  const {
    contract,
    chainId,
    abi: t0,
    hideMintBtn
  } = opts;
  const abi = t0 === undefined ? MEMES_MANIFOLD_PROXY_ABI : t0;
  const [prevNftId, setPrevNftId] = useState(nftId);
  if (nftId !== prevNftId) {
    setPrevNftId(nftId);
    setErrorFromCallback(false);
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setErrorFromCallback(true);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleManifoldClaimError = t1;
  let t2;
  if ($[1] !== abi || $[2] !== chainId || $[3] !== contract || $[4] !== nftId) {
    t2 = {
      chainId,
      contract,
      proxy: MANIFOLD_LAZY_CLAIM_CONTRACT,
      abi,
      identifier: nftId,
      onError: handleManifoldClaimError
    };
    $[1] = abi;
    $[2] = chainId;
    $[3] = contract;
    $[4] = nftId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    claim: manifoldClaim
  } = useManifoldClaim(t2);
  const isError = errorFromCallback && (!manifoldClaim || manifoldClaim.isError);
  const {
    isIos
  } = useCapacitor();
  const {
    country
  } = useCookieConsent();
  const [now, setNow] = useState(_temp);
  const showMintBtn = !hideMintBtn && !(isIos && country !== "US");
  let t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const interval = globalThis.window.setInterval(() => {
        setNow(Time.now());
      }, 1000);
      return () => {
        globalThis.window.clearInterval(interval);
      };
    };
    t4 = [];
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  bb0: {
    if (isError) {
      let t6;
      if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = {
          type: "error"
        };
        $[8] = t6;
      } else {
        t6 = $[8];
      }
      t5 = t6;
      break bb0;
    }
    if (!manifoldClaim) {
      let t6;
      if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = {
          type: "loading"
        };
        $[9] = t6;
      } else {
        t6 = $[9];
      }
      t5 = t6;
      break bb0;
    }
    if (manifoldClaim.isSoldOut) {
      let t6;
      if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = {
          type: "sold_out"
        };
        $[10] = t6;
      } else {
        t6 = $[10];
      }
      t5 = t6;
      break bb0;
    }
    if (manifoldClaim.isFinalized && !manifoldClaim.isDropComplete && manifoldClaim.nextMemePhase) {
      const nextPhase = manifoldClaim.nextMemePhase;
      let t6;
      if ($[11] !== nextPhase.start || $[12] !== now) {
        t6 = now.lt(nextPhase.start);
        $[11] = nextPhase.start;
        $[12] = now;
        $[13] = t6;
      } else {
        t6 = $[13];
      }
      const hasReachedNextPhaseStart = !t6;
      const t7 = `${nextPhase.name} Starts In`;
      let t8;
      if ($[14] !== nextPhase.start) {
        t8 = nextPhase.start.toSeconds();
        $[14] = nextPhase.start;
        $[15] = t8;
      } else {
        t8 = $[15];
      }
      const t9 = nextPhase.type === ManifoldPhase.ALLOWLIST;
      const t10 = showMintBtn && !hasReachedNextPhaseStart;
      let t11;
      if ($[16] !== t10 || $[17] !== t7 || $[18] !== t8 || $[19] !== t9) {
        t11 = {
          type: "countdown",
          countdown: {
            title: t7,
            targetDate: t8,
            showAllowlistInfo: t9,
            showMintBtn: t10,
            isActive: false
          }
        };
        $[16] = t10;
        $[17] = t7;
        $[18] = t8;
        $[19] = t9;
        $[20] = t11;
      } else {
        t11 = $[20];
      }
      t5 = t11;
      break bb0;
    }
    if (manifoldClaim.isDropComplete) {
      let t6;
      if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = {
          type: "finalized"
        };
        $[21] = t6;
      } else {
        t6 = $[21];
      }
      t5 = t6;
      break bb0;
    }
    const phaseName = manifoldClaim.phase === ManifoldPhase.ALLOWLIST && manifoldClaim.memePhase ? manifoldClaim.memePhase.name : manifoldClaim.phase;
    const isUpcoming = manifoldClaim.status === ManifoldClaimStatus.UPCOMING;
    const t6 = `${phaseName} ${isUpcoming ? "Starts In" : "Ends In"}`;
    const t7 = isUpcoming ? manifoldClaim.startDate : manifoldClaim.endDate;
    const t8 = manifoldClaim.phase === ManifoldPhase.ALLOWLIST;
    const t9 = !isUpcoming;
    let t10;
    if ($[22] !== showMintBtn || $[23] !== t6 || $[24] !== t7 || $[25] !== t8 || $[26] !== t9) {
      t10 = {
        type: "countdown",
        countdown: {
          title: t6,
          targetDate: t7,
          showAllowlistInfo: t8,
          showMintBtn,
          isActive: t9
        }
      };
      $[22] = showMintBtn;
      $[23] = t6;
      $[24] = t7;
      $[25] = t8;
      $[26] = t9;
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    t5 = t10;
  }
  return t5;
}
function _temp() {
  return Time.now();
}